import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Plus, Mail, X, ShieldCheck, User, Trash2, MoreVertical } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABELS = { owner: "Owner", admin: "Administrator", editor: "Editor", viewer: "Viewer" };

export default function Team() {
  const { activeWorkspace, user, canAdmin } = useAuth();
  const [members, setMembers] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ email: "", role: "editor" });
  const [menu, setMenu] = useState(null);

  const load = () => api.get(`/workspaces/${activeWorkspace.id}/members`).then(({ data }) => setMembers(data || []));
  useEffect(() => { if (activeWorkspace) load(); /* eslint-disable-next-line */ }, [activeWorkspace]);

  const invite = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/workspaces/${activeWorkspace.id}/invites`, form);
      toast.success("Invitation sent");
      setShow(false);
      setForm({ email: "", role: "editor" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not invite"); }
  };

  const changeRole = async (memberUserId, role) => {
    setMenu(null);
    try {
      await api.patch(`/workspaces/${activeWorkspace.id}/members/${memberUserId}`, { role });
      toast.success(`Role updated to ${ROLE_LABELS[role]}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not change role"); }
  };

  const removeMember = async (memberUserId, name) => {
    setMenu(null);
    if (!window.confirm(`Remove ${name || "this member"} from the workspace?`)) return;
    try {
      await api.delete(`/workspaces/${activeWorkspace.id}/members/${memberUserId}`);
      toast.success("Member removed");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not remove"); }
  };

  const revokeInvite = async (inviteId) => {
    setMenu(null);
    if (!window.confirm("Revoke this pending invitation?")) return;
    try {
      await api.delete(`/workspaces/${activeWorkspace.id}/invites/${inviteId}`);
      toast.success("Invite revoked");
      load();
    } catch { toast.error("Could not revoke"); }
  };

  return (
    <div className="max-w-5xl mx-auto p-8" data-testid="team-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold text-white">Team</h1>
          <p className="text-sm text-slate-400">Manage members, roles and pending invitations.</p>
        </div>
        {canAdmin ? (
          <button onClick={() => setShow(true)} className="fx-btn-primary text-white text-sm font-medium px-4 py-2 rounded-lg inline-flex items-center gap-2" data-testid="invite-btn">
            <Plus size={14} /> Invite member
          </button>
        ) : (
          <span className="text-[11px] mono uppercase tracking-widest px-2 py-1 rounded border border-slate-700 text-slate-400">Read-only for {ROLE_LABELS[activeWorkspace?.role] || "your role"}</span>
        )}
      </div>

      <div className="fx-panel overflow-visible">
        <div className="overflow-x-auto lg:overflow-visible">
        <table className="w-full text-sm min-w-[520px] lg:min-w-0">
          <thead className="bg-[#0A0F1F] text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {members.map((m, i) => {
              const isSelf = m.member_user_id && m.member_user_id === user?.id;
              const isOwner = m.role === "owner";
              const canManage = canAdmin && !isOwner && !isSelf;
              return (
                <tr key={i} className="border-t border-[#1E293B]" data-testid={`member-row-${i}`}>
                  <td className="px-4 py-3 text-slate-100 inline-flex items-center gap-2">
                    {isOwner ? <ShieldCheck size={13} className="text-cyan-300" /> : <User size={13} className="text-slate-500" />}
                    {m.first_name} {m.last_name || ""} {isSelf && <span className="text-[10px] mono text-slate-500 uppercase">(You)</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-400">{m.email}</td>
                  <td className="px-4 py-3 text-slate-300">{ROLE_LABELS[m.role] || m.role}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full border ${m.status === "active" ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" : "bg-amber-500/10 text-amber-300 border-amber-500/30"}`}>
                      {m.status}
                    </span>
                  </td>
                  <td className="px-2 relative">
                    {canManage && m.status === "active" && (
                      <>
                        <button onClick={(e) => { e.stopPropagation(); setMenu(menu === i ? null : i); }} className="text-slate-500 hover:text-white p-1" data-testid={`member-menu-${i}`}><MoreVertical size={14} /></button>
                        {menu === i && (
                          <div className="absolute right-2 top-full mt-1 fx-panel-2 rounded-lg shadow-xl w-44 z-30">
                            <div className="px-3 py-2 text-[10px] mono uppercase tracking-widest text-slate-500 border-b border-[#1E293B]">Change role</div>
                            {["admin", "editor", "viewer"].map((r) => (
                              <button key={r} onClick={() => changeRole(m.member_user_id, r)} disabled={m.role === r} className="w-full text-left px-3 py-2 text-xs text-slate-200 hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed" data-testid={`role-set-${i}-${r}`}>
                                {ROLE_LABELS[r]}
                              </button>
                            ))}
                            <div className="border-t border-[#1E293B]" />
                            <button onClick={() => removeMember(m.member_user_id, `${m.first_name} ${m.last_name || ""}`.trim())} className="w-full text-left px-3 py-2 text-xs text-red-300 hover:bg-white/5 inline-flex items-center gap-2" data-testid={`member-remove-${i}`}><Trash2 size={12} /> Remove from workspace</button>
                          </div>
                        )}
                      </>
                    )}
                    {canAdmin && m.status === "invited" && (
                      <button onClick={() => revokeInvite(m.invite_id)} className="text-slate-500 hover:text-red-400 p-1" data-testid={`invite-revoke-${i}`} title="Revoke invite"><Trash2 size={14} /></button>
                    )}
                  </td>
                </tr>
              );
            })}
            {members.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-sm text-slate-500">No members yet.</td></tr>}
          </tbody>
        </table>
        </div>
      </div>

      {show && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-40" onClick={() => setShow(false)}>
          <div className="fx-panel p-6 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()} data-testid="invite-dialog">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-lg font-semibold">Invite member</div>
              <button onClick={() => setShow(false)} className="text-slate-500 hover:text-white"><X size={16} /></button>
            </div>
            <form onSubmit={invite} className="space-y-3">
              <label className="block">
                <div className="text-xs text-slate-400 mb-1">Email</div>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-[#0D1224] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-slate-100" data-testid="invite-email" />
              </label>
              <label className="block">
                <div className="text-xs text-slate-400 mb-1">Role</div>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full bg-[#0D1224] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-slate-100" data-testid="invite-role">
                  <option value="admin">Administrator — full control except owner</option>
                  <option value="editor">Editor — create and edit documents</option>
                  <option value="viewer">Viewer — read-only access</option>
                </select>
              </label>
              <button className="fx-btn-primary w-full text-white font-medium py-2 rounded-lg inline-flex items-center justify-center gap-2" data-testid="invite-submit"><Mail size={14} /> Send invitation</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
